import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  ArticleCatalog,
  ArticleInventaireDto,
  ArticleSousReferenceInventaireDto,
  StockCatalogResponse,
} from '../../shared/models';
import { StorageService } from './storage.service';
import { environment } from '../../../environments/environment';

export interface ResolvedCatalogEntry {
  isSubReference: boolean;
  article: ArticleInventaireDto;
  sousRef?: ArticleSousReferenceInventaireDto;
}

@Injectable({ providedIn: 'root' })
export class ArticleCatalogService {
  private catalog: ArticleCatalog | null = null;
  private byBarcode = new Map<string, ResolvedCatalogEntry>();
  private catalogSubject = new BehaviorSubject<ArticleCatalog | null>(null);
  catalog$ = this.catalogSubject.asObservable();

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
  ) {}

  get current(): ArticleCatalog | null {
    return this.catalog;
  }

  fetchCatalog(
    idInventaire: number,
    codeBu: string,
    codeSite: string,
  ): Observable<ArticleCatalog> {
    const catalog$ = this.http.get<ArticleCatalog>(
      `${environment.apiBaseUrl}/common/inventaire/get-article-scannable`,
      { params: { codeSite, codeBu } },
    );
    const stock$ = this.fetchStock(codeSite, codeBu);

    return forkJoin([catalog$, stock$]).pipe(
      map(([catalog, stock]) => this.mergeStock(catalog, stock)),
      switchMap((merged) =>
        from(this.storageService.saveCatalog(idInventaire, merged)).pipe(
          tap(() => this.setCatalog(merged)),
          switchMap(() => of(merged)),
        ),
      ),
    );
  }

  fetchPreviewCatalog(codeBu: string, codeSite: string): Observable<ArticleCatalog> {
    const catalog$ = this.http.get<ArticleCatalog>(
      `${environment.apiBaseUrl}/common/inventaire/get-article-scannable`,
      { params: { codeSite, codeBu } },
    );
    const stock$ = this.fetchStock(codeSite, codeBu);
    return forkJoin([catalog$, stock$]).pipe(
      map(([catalog, stock]) => this.mergeStock(catalog, stock)),
    );
  }

  private fetchStock(codeSite: string, codeBu: string): Observable<StockCatalogResponse> {
    return this.http
      .get<StockCatalogResponse>(
        `${environment.apiBaseUrl}/common/inventaire/get-stock-scannable`,
        { params: { codeSite, codeBu } },
      )
      .pipe(
        catchError((err) => {
          console.warn('[ArticleCatalogService] stock fetch failed, catalog will load without stock', err);
          return of<StockCatalogResponse>({
            articleStocks: [],
            sousRefStocks: [],
            codeSite,
            generatedAt: Date.now(),
          });
        }),
      );
  }

  private mergeStock(catalog: ArticleCatalog, stock: StockCatalogResponse): ArticleCatalog {
    const byCodeArticle = new Map<string, { qtLogicielle?: number }>();
    for (const s of stock.articleStocks) {
      if (s.codeArticle) {
        byCodeArticle.set(s.codeArticle, { qtLogicielle: s.qtLogicielle });
      }
    }
    const bySousRefId = new Map<number, { qtLogicielle?: number }>();
    for (const s of stock.sousRefStocks) {
      if (s.idArticleSousReference != null) {
        bySousRefId.set(s.idArticleSousReference, {
          qtLogicielle: s.qtLogicielle,
        });
      }
    }

    const articles: ArticleInventaireDto[] = catalog.articles.map((art) => {
      const sousReferences: ArticleSousReferenceInventaireDto[] = art.sousReferences.map((sr) => {
        const srStock = bySousRefId.get(sr.idArticleSousReference);
        return srStock
          ? { ...sr, qtLogicielle: srStock.qtLogicielle, hasStockLine: true }
          : sr;
      });
      if (art.hasSubReferences) {
        return { ...art, sousReferences };
      }
      const artStock = byCodeArticle.get(art.codeArticle);
      return artStock
        ? { ...art, sousReferences, qtLogicielle: artStock.qtLogicielle, hasStockLine: true }
        : { ...art, sousReferences };
    });

    return { ...catalog, articles };
  }

  async loadFromStorage(idInventaire: number): Promise<ArticleCatalog | null> {
    const catalog = await this.storageService.getCatalog(idInventaire);
    if (catalog) this.setCatalog(catalog);
    return catalog;
  }

  setCatalog(catalog: ArticleCatalog): void {
    this.catalog = catalog;
    this.rebuildIndex();
    this.catalogSubject.next(catalog);
  }

  resolve(barcode: string): ResolvedCatalogEntry | null {
    return this.byBarcode.get(barcode) ?? null;
  }

  clear(): void {
    this.catalog = null;
    this.byBarcode.clear();
    this.catalogSubject.next(null);
  }

  async clearStored(idInventaire: number): Promise<void> {
    this.clear();
    await this.storageService.clearCatalog(idInventaire);
  }

  private rebuildIndex(): void {
    this.byBarcode.clear();
    if (!this.catalog) return;
    for (const art of this.catalog.articles) {
      if (art.hasSubReferences) {
        for (const sr of art.sousReferences) this.indexSubRef(art, sr);
      } else {
        this.indexArticle(art);
      }
    }
  }

  private indexArticle(art: ArticleInventaireDto): void {
    const key = art.codeEan?.trim() || art.codeBarre?.trim();
    if (!key) return;
    this.byBarcode.set(key, { isSubReference: false, article: art });
  }

  private indexSubRef(
    art: ArticleInventaireDto,
    sr: ArticleSousReferenceInventaireDto,
  ): void {
    const key = sr.codeEan?.trim() || sr.codeBarre?.trim();
    if (!key) return;
    this.byBarcode.set(key, { isSubReference: true, article: art, sousRef: sr });
  }
}
